import React from 'react';

interface Insight {
  id: string;
  type: 'success' | 'warning' | 'info' | 'urgent';
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface SmartInsightsPanelProps {
  insights: Insight[];
  loading?: boolean;
}

const SmartInsightsPanel: React.FC<SmartInsightsPanelProps> = ({ insights, loading }) => {
  const getInsightStyles = (type: Insight['type']) => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-emerald-50',
          border: 'border-emerald-200',
          icon: 'text-emerald-600',
          text: 'text-emerald-900',
          badge: 'bg-emerald-100 text-emerald-700'
        };
      case 'warning':
        return {
          bg: 'bg-amber-50',
          border: 'border-amber-200',
          icon: 'text-amber-600',
          text: 'text-amber-900',
          badge: 'bg-amber-100 text-amber-700'
        };
      case 'urgent':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          icon: 'text-red-600',
          text: 'text-red-900',
          badge: 'bg-red-100 text-red-700'
        };
      case 'info':
      default:
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          icon: 'text-blue-600',
          text: 'text-blue-900',
          badge: 'bg-blue-100 text-blue-700'
        };
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(2)].map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="h-20 bg-gray-100 rounded-xl" />
          </div>
        ))}
      </div>
    );
  }

  if (insights.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-3">
          <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </div>
        <p className="text-sm text-gray-500">No insights available</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {insights.map((insight) => {
        const styles = getInsightStyles(insight.type);
        
        return (
          <div
            key={insight.id}
            className={`rounded-xl border ${styles.border} ${styles.bg} p-4 transition-all hover:shadow-sm`}
          >
            <div className="flex gap-3">
              <div className={`flex-shrink-0 ${styles.icon}`}>
                {insight.icon}
              </div>
              
              <div className="flex-1 min-w-0">
                <h4 className={`text-sm font-semibold ${styles.text} mb-1`}>
                  {insight.title}
                </h4>
                <p className="text-sm text-gray-600 mb-3">
                  {insight.description}
                </p>
                
                {insight.action && (
                  <button
                    onClick={insight.action.onClick}
                    className={`text-xs font-medium ${styles.icon} hover:underline`}
                  >
                    {insight.action.label} →
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SmartInsightsPanel;
